import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import type { StrapiEventListPage } from '~/composables/types/strapiEventListPage'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import { useStrapiConfig, buildStrapiUrl } from '~/composables/useStrapi'
import { encodeLocationTypeSlug } from '~/composables/transactionalPage/useLocationSlugParser'
import { strapiEventListPageGetters } from '~/utils/strapiEventListPageGetters'

export interface FilterNavigationParams {
  /** Route slug used for the Strapi existence check and transactional base path. */
  categorySlug?: string
  /** Full current filter state — the single source of truth for query params. */
  filters: SearchFilterOptions
  /**
   * When set, Strapi lookup uses `filters[slug][$eq]` (teamevent / tag-style pages),
   * mirroring `getEventListPageStrapiURL` — mutually exclusive with the default
   * city + categorySlug existence query.
   */
  strapiListPageSlug?: string
  /**
   * When true, skip the Strapi existence check and always return the search
   * fallback path. Use when the selected filters are known to have no
   * single matching transactional page (e.g. multiple categories selected).
   */
  forceSearch?: boolean
}

export interface FilterNavigationResult {
  /** Locale-prefixed path to navigate to */
  path: string
  /** true when a matching Strapi event-list page was found */
  isTransactional: boolean
}

const FETCH_TIMEOUT_MS = 8_000

// Values that represent "empty" or "unset" and must not appear in the URL.
const isEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null) return true
  if (value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

// Keys that match getEmptyFilters() defaults and carry no information.
const DEFAULT_FILTER_VALUES: Record<string, unknown> = {
  priceMin: 0,
  priceMax: 1200,
  hasAvailableDates: false,
  hasPromotion: false,
}

/**
 * Serialize a SearchFilterOptions object into a URLSearchParams, skipping
 * empty/default values and any explicitly excluded keys.
 */
const serializeFilters = (
  filters: SearchFilterOptions,
  excludeKeys: Set<string> = new Set()
): URLSearchParams => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (excludeKeys.has(key)) continue
    if (isEmpty(value)) continue
    if (DEFAULT_FILTER_VALUES[key] === value) continue

    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (!isEmpty(v)) params.append(key, String(v))
      })
    } else {
      params.set(key, String(value))
    }
  }
  return params
}

/**
 * Build a minimal Strapi query for a single event-list-pages existence check.
 * Only fetches id + citySlug + categorySlug + type; no heavy populate.
 */
const buildExistenceCheckUrl = (
  locationSlug: string | undefined,
  categorySlug: string | undefined
): string => {
  const params = new URLSearchParams()
  params.set('fields[0]', 'id')
  params.set('fields[1]', 'citySlug')
  params.set('fields[2]', 'categorySlug')
  params.set('fields[3]', 'type')
  params.set('pagination[limit]', '1')

  if (locationSlug) {
    params.set('filters[citySlug][$eq]', locationSlug)
  } else {
    params.set('filters[citySlug][$null]', 'true')
  }

  if (categorySlug) {
    params.set('filters[categorySlug][$eq]', categorySlug)
  } else {
    params.set('filters[categorySlug][$null]', 'true')
  }

  return 'event-list-pages?' + params.toString()
}

/**
 * Slug-based event-list page lookup (teamevent, `/t/` tag pages, etc.),
 * aligned with `getEventListPageStrapiURL` when `slug` is provided.
 */
const buildSlugBasedExistenceCheckUrl = (listPageSlug: string): string => {
  const params = new URLSearchParams()
  params.set('fields[0]', 'id')
  params.set('fields[1]', 'citySlug')
  params.set('fields[2]', 'categorySlug')
  params.set('fields[3]', 'type')
  params.set('fields[4]', 'slug')
  params.set('pagination[limit]', '1')
  params.set('filters[slug][$eq]', listPageSlug)
  params.set('filters[citySlug][$null]', 'true')
  params.set('filters[categorySlug][$null]', 'true')
  return 'event-list-pages?' + params.toString()
}

type MinimalPage = {
  id: number
  citySlug: string | null
  categorySlug: string | null
  type: string
  slug?: string | null
}

// Keys encoded in the route path — must not appear as query params on
// transactional pages or on the search fallback.
const LOCATION_KEYS = new Set(['location', 'locationType'])

export const useFilterNavigation = () => {
  const localePath = useLocalePath()
  const strapiConfig = useStrapiConfig()

  /**
   * Determine the Strapi citySlug to use for the existence check.
   * For non-city location types, encodes the composite slug (e.g. 'berlin--r').
   */
  const buildTargetLocationSlug = (
    locationSlug: string | undefined,
    locationType: string | undefined
  ): string | undefined => {
    if (!locationSlug) return undefined
    return encodeLocationTypeSlug(locationSlug, locationType)
  }

  /**
   * Construct the storefront path for a confirmed Strapi page, appending any
   * filters not already encoded in the route as query params.
   */
  const buildTransactionalPath = (
    page: MinimalPage,
    params: FilterNavigationParams
  ): string => {
    const pageForPath = page as StrapiEventListPage
    const pathType = page.type

    if (
      pathType === 'teamevent' ||
      pathType === 'general' ||
      pathType === 'kits'
    ) {
      const rawPath =
        strapiEventListPageGetters.getKonfettiPagePath(pageForPath)
      const basePath = rawPath.endsWith('/') ? rawPath : `${rawPath}/`
      const excludeKeys = new Set(LOCATION_KEYS)
      const category = page.categorySlug ?? params.categorySlug
      if (category) excludeKeys.add('categories')
      const qs = serializeFilters(params.filters, excludeKeys).toString()
      return qs ? `${basePath}?${qs}` : basePath
    }

    const city = page.citySlug ?? params.filters.location
    const category = page.categorySlug ?? params.categorySlug

    let basePath: string
    if (city && category) basePath = `/${city}/${category}/`
    else if (category) basePath = `/c/${category}/`
    else if (city) basePath = `/${city}/`
    else basePath = '/search/'

    // location/locationType are in the route path; strip them from query params.
    // When a category segment is already in the URL, `filters.categories` is
    // the page default — omit it so we do not keep redundant ?categories=…
    const excludeKeys = new Set(LOCATION_KEYS)
    if (category) excludeKeys.add('categories')

    const qs = serializeFilters(params.filters, excludeKeys).toString()
    return qs ? `${basePath}?${qs}` : basePath
  }

  /**
   * Build the search fallback URL. Serializes the full filter state directly —
   * location, categories (display names), and all other active filters become
   * query params that the search page reads natively.
   */
  const buildSearchPath = (params: FilterNavigationParams): string => {
    const qs = serializeFilters(params.filters).toString()
    return qs ? `/search/?${qs}` : '/search/'
  }

  /**
   * Check Strapi for a matching event-list page and return the resolved path.
   * - If found: returns the transactional page path (with extra filters as query params)
   * - If not found or error: returns the search path (with all filters as query params)
   */
  const resolve = async (
    params: FilterNavigationParams
  ): Promise<FilterNavigationResult> => {
    if (params.forceSearch) {
      return {
        path: localePath(buildSearchPath(params)),
        isTransactional: false,
      }
    }

    const urlSuffix = params.strapiListPageSlug
      ? buildSlugBasedExistenceCheckUrl(params.strapiListPageSlug)
      : buildExistenceCheckUrl(
          buildTargetLocationSlug(
            params.filters.location,
            params.filters.locationType
          ),
          params.categorySlug
        )
    const url = buildStrapiUrl(strapiConfig, urlSuffix)

    try {
      const response = await $fetch<StrapiApiDefaultResponse<MinimalPage[]>>(
        url,
        {
          headers: strapiConfig.headers,
          timeout: FETCH_TIMEOUT_MS,
        }
      )

      const pages = response?.data ?? []
      if (pages.length > 0) {
        const path = buildTransactionalPath(pages[0]!, params)
        return {
          path: localePath(path),
          isTransactional: true,
        }
      }
    } catch {
      // Network or Strapi error — fall through to search
    }

    return {
      path: localePath(buildSearchPath(params)),
      isTransactional: false,
    }
  }

  return { resolve }
}
